import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ShieldWarning, Camera, VideoCamera,
  Lightning, House, Buildings, Microphone, Stop, Trash, CheckCircle,
  XCircle, Spinner, Warning, MagnifyingGlass, FloppyDisk, PaperPlaneTilt,
  CalendarBlank, Wrench, Package, Image as ImageIcon, FileArrowUp,
  WarningCircle, Info, Sparkle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import AuthVideo from "@/components/AuthVideo";
import { extractErr } from "@/lib/errors";
import AccountMenu from "@/components/AccountMenu";
import { HelpPill } from "@/components/HeaderBrand";
import OfflineDraftsPill from "@/components/OfflineDraftsPill";
import LiveCameraCapture from "@/components/LiveCameraCapture";
import { MeasurementsSet, isMeasurementsSetComplete } from "@/components/MeasurementCapture";
import { useUndoable } from "@/hooks/use-undoable";
import { useT, useI18n } from "@/lib/i18n";
import useOfflineQueue from "@/lib/useOfflineQueue";
import { useWizardDraft, loadDraft, clearDraft } from "@/lib/useWizardDraft";
import { toast } from "sonner";

/**
 * Warranty claim wizard — End-User / Dealer files a warranty claim against
 * a specific compressor (Products_By_Serial).
 *
 *   Step 0  — pick compressor
 *   Step 1  — nameplate (photo)
 *   Step 2  — installation video (checklist of what to capture)
 *   Step 3  — voltage measurements (phase-to-phase photos, 3 expected)
 *   Step 4  — nearby devices video (transformers, HVAC, furnace)
 *   Step 5  — working-hours photo (only if no_internet)
 *   Step 6  — findings explanation (text + optional voice + Whisper)
 *   Step 7  — supporting evidence (optional)
 *   Step 8  — emergency-action acknowledgement
 *   Step 9  — review & submit
 *   DONE    — ticket #, parts purchased, missing PM parts
 *
 * Storage / API: see /app/backend/warranty_routes.py.
 */

const STEPS = [
  "pick", "nameplate", "installation_video", "voltage", "amps", "nearby_devices",
  "working_hours", "findings", "supporting", "warranty_parts", "emergency", "review", "done",
];

/** iter150AG.photo-meas — phase count derived from the picked
 *  compressor's product type string (e.g. "220v 3Phz" or "220v 1Phz").
 *  Returns 1 or 3. Defaults to 3 if ambiguous (matches our historic
 *  default — never causes a *less* strict requirement). */
function _phaseCountFromClaim(claim) {
  const blob = `${claim?.product_type || ""} ${claim?.product_name || ""}`.toLowerCase();
  if (/\b1\s*phz\b|\b1\s*phase\b|\bsingle[\s-]*phase\b/.test(blob)) return 1;
  return 3;
}

const KIND_META = {
  nameplate: { label: "Nameplate", desc: "Photo of the metal nameplate showing the serial number and model.", accept: "image/*", isVideo: false, multiple: false },
  installation_video: { label: "Installation video", desc: "One walk-through video covering: room compressor is in, inside the unit, outside the unit, ventilation (vent ducting OR a large room with sufficient airflow — must be visible), fusable disconnect, line reactor (if /D model), electric box, and outlet connections to the tank including dryer, filters, and valves up to the first tank.", accept: "video/*", isVideo: true, multiple: true },
  voltage: { label: "Voltage measurements", desc: "Phase-to-phase voltage readings — typically 3 photos (L1↔L2, L2↔L3, L1↔L3).", accept: "image/*", isVideo: false, multiple: true },
  nearby_devices: { label: "Nearby devices", desc: "Walk-around video of nearby loads — large transformers, HVAC, furnace, etc.", accept: "video/*", isVideo: true, multiple: true },
  working_hours: { label: "Working hours photo", desc: "Live photo of the controller display showing total/loaded/unloaded hours. If the compressor cannot be turned on, mark the box below to skip this step.", accept: "image/*", isVideo: false, multiple: false },
  supporting: { label: "Supporting evidence", desc: "Any extra photos or videos that explain the failure or support the claim. Optional but encouraged.", accept: "image/*,video/*", isVideo: false, multiple: true },
  warranty_parts: { label: "Warranty part(s)", desc: "Live photo or short video of the actual part(s) being claimed. Capture serial numbers / part numbers if visible.", accept: "image/*,video/*", isVideo: false, multiple: true },
};

export default function WarrantyClaimPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const { enqueue: enqueueIfOfflineRaw } = useOfflineQueue();
  // Resolve relative API paths against REACT_APP_BACKEND_URL because
  // the SW outbox needs an absolute URL to fetch later.
  const enqueueIfOffline = useCallback(async (path, init) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = path.startsWith("http") ? path : `${base}/api${path.startsWith("/") ? "" : "/"}${path}`;
    return enqueueIfOfflineRaw(url, init);
  }, [enqueueIfOfflineRaw]);

  // wizard
  const [stepIdx, setStepIdx] = useState(0);

  // pick
  const [pickQuery, setPickQuery] = useState("");
  const [fleet, setFleet] = useState({ items: [], loading: false });
  const [selected, setSelected] = useState(null);

  // claim
  const [claim, setClaim] = useState(null);   // server-issued claim doc
  const [media, setMedia] = useState([]);     // [{id, kind, filename, url, mime_type, size}]
  const [uploading, setUploading] = useState(null); // current kind being uploaded

  // findings
  const [findingsText, setFindingsText] = useState("");
  const [transcript, setTranscript] = useState("");
  const [recording, setRecording] = useState(null); // MediaRecorder ref-ish state object
  const [recDuration, setRecDuration] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const recTickRef = useRef(null);

  // emergency
  const [emergency, setEmergency] = useState(null); // 'accepted' | 'refused' | null
  const [emergencyShownDisclaimer, setEmergencyShownDisclaimer] = useState(false);

  // iter150AZ.69 — Phase 5. Persist wizard state to localStorage so a
  // tab close mid-wizard doesn't lose the tech's progress.
  useWizardDraft("warranty",
    { selected, stepIdx, claim, media, findingsText, transcript,
      emergency, emergencyShownDisclaimer },
    [selected, stepIdx, claim, media, findingsText, transcript,
     emergency, emergencyShownDisclaimer]);

  // Restore on mount, only when the user hasn't navigated in with a
  // server claim in URL.
  useEffect(() => {
    const draft = loadDraft("warranty");
    if (!draft) return;
    if (draft.selected)     setSelected(draft.selected);
    if (draft.stepIdx != null) setStepIdx(draft.stepIdx);
    if (draft.claim)        setClaim(draft.claim);
    if (Array.isArray(draft.media)) setMedia(draft.media);
    if (draft.findingsText) setFindingsText(draft.findingsText);
    if (draft.transcript)   setTranscript(draft.transcript);
    if (draft.emergency)    setEmergency(draft.emergency);
    if (draft.emergencyShownDisclaimer != null) setEmergencyShownDisclaimer(draft.emergencyShownDisclaimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // misc
  const [noInternet, setNoInternet] = useState(false);
  const [cantTurnOn, setCantTurnOn] = useState(false);
  const [nameplateAi, setNameplateAi] = useState(null);
  const [voiceAi, setVoiceAi] = useState(null);
  const [voiceAiLoading, setVoiceAiLoading] = useState(false);
  const [voiceAudioUrl, setVoiceAudioUrl] = useState(null);
  const [evidenceSuggestions, setEvidenceSuggestions] = useState([]);
  // iter142: AI agent (rule-driven) — different from the curated library
  // suggestions above.  Drives the "Required evidence" checklist + the
  // "Need a representative" override.
  const [evidenceAgent, setEvidenceAgent] = useState(null);
  const [evidenceAgentLoading, setEvidenceAgentLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [finalResult, setFinalResult] = useState(null);

  const step = STEPS[stepIdx];

  // Resolve current claim on changes (if any).
  const refreshClaim = useCallback(async () => {
    if (!claim?.id) return;
    try {
      const r = await api.get(`/warranty/claims/${claim.id}`);
      setClaim(r.data?.claim || claim);
      setMedia(r.data?.media || []);
    } catch (_) { /* ignore */ }
  }, [claim?.id]);

  useEffect(() => { refreshClaim(); }, [refreshClaim]);

  // ── Existing-drafts list (only relevant on the pick step) ──
  // Reps occasionally start a claim, get distracted, and never finish — so
  // the wizard creates fresh drafts indefinitely. We surface "your unfinished
  // drafts" right above the fleet list with Resume + Discard actions so they
  // don't accumulate forever.
  const [myDrafts, setMyDrafts] = useState([]);
  const refreshMyDrafts = useCallback(async () => {
    try {
      const r = await api.get(`/warranty/claims`, { params: { status: "draft", limit: 25 } });
      const meEmail = (user?.email || "").toLowerCase();
      // The backend returns ALL accessible drafts to staff; scope to ones I
      // personally started so the list stays useful (other staff's drafts
      // would be noise here).
      const mine = (r.data?.items || []).filter(
        (d) => (d.created_by || "").toLowerCase() === meEmail,
      );
      setMyDrafts(mine);
    } catch (_) { setMyDrafts([]); }
  }, [user?.email]);
  useEffect(() => { if (step === "pick") refreshMyDrafts(); }, [step, refreshMyDrafts]);

  // Fleet search
  useEffect(() => {
    let cancelled = false;
    setFleet((p) => ({ ...p, loading: true }));
    api.get(`/service-tech/fleet`, { params: { q: pickQuery, limit: 50 } })
      .then((r) => { if (!cancelled) setFleet({ items: r.data?.items || [], loading: false }); })
      .catch(() => { if (!cancelled) setFleet({ items: [], loading: false }); });
    return () => { cancelled = true; };
  }, [pickQuery]);

  // Once claim is created, ensure findingsText reflects server (allows resume).
  useEffect(() => {
    if (claim?.findings_text != null) setFindingsText(claim.findings_text || "");
    if (claim?.findings_transcript != null) setTranscript(claim.findings_transcript || "");
    if (claim?.no_internet != null) setNoInternet(Boolean(claim.no_internet));
    if (claim?.cant_turn_on != null) setCantTurnOn(Boolean(claim.cant_turn_on));
    if (claim?.emergency_action) setEmergency(claim.emergency_action);
  }, [claim?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── helpers ──
  const mediaByKind = useMemo(() => {
    const m = {};
    for (const item of media) (m[item.kind] = m[item.kind] || []).push(item);
    return m;
  }, [media]);

  // iter136.31 / iter136.32 — error-message extraction lives in
  // ``@/lib/errors`` so every flow (warranty, conversations, logbook,
  // …) renders a useful message instead of ``[object Object]``. See
  // ``startClaim`` below for the structured-detail / CTA pattern.

  const startClaim = async () => {
    if (!selected) return;
    // iter150AZ.68 — offline-first warranty claim. Same UUID-up-front
    // pattern as Commissioning (iter150AZ.66) and PM Service Visit
    // (Phase 3): the field tech can begin filing a claim while
    // offline and queued media uploads target this client UUID.
    const offlineClaimId = (
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `off-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    ).replace(/-/g, "");
    const payload = {
      serial_id: selected.serial_id,
      claim_id:  offlineClaimId,
    };
    const decision = await enqueueIfOffline("/warranty/claims", {
      method: "POST",
      headers: { "Content-Type": "application/json",
                  "X-User-Email": user?.email || "" },
      body:   JSON.stringify(payload),
    });
    if (decision === "queued") {
      // Build a minimal optimistic claim doc so the wizard can move
      // forward. ``commissioning_required`` gating relies on the
      // server — if the compressor lacks a commissioning signature,
      // the queued POST will 409 at flush time and the claim won't
      // persist. We accept that as the trade-off for letting the
      // tech keep capturing.
      setClaim({
        id: offlineClaimId,
        serial_id: selected.serial_id,
        serial_number: selected.serial_number || "(offline)",
        product_name: selected.product_name || "",
        company_name: selected.company_name || "",
        status: "draft",
        created_by: user?.email || "",
        created_at: new Date().toISOString(),
        media_kinds_present: [],
        no_internet: false,
        emergency_action: null,
        _offline: true,
      });
      setStepIdx(1);
      toast.info(t("offline.queued_claim",
                    "Saved offline — will sync when you're back online"));
      return;
    }
    try {
      const r = await api.post("/warranty/claims", payload);
      setClaim(r.data);
      setStepIdx(1);
    } catch (e) {
      const { message, nextStep, code } = extractErr(e, "Failed to start claim");
      // Commissioning-required is a known business gate — surface it as
      // a toast with a "Go to commissioning" CTA instead of a blocking
      // ``alert()``. Otherwise fall back to a normal toast.
      if (code === "commissioning_required" && nextStep) {
        toast.error(message, {
          duration: 12000,
          action: {
            label: "Open commissioning",
            onClick: () => navigate(nextStep),
          },
        });
      } else {
        toast.error(message);
      }
    }
  };

  const resumeDraft = async (draftId) => {
    try {
      const r = await api.get(`/warranty/claims/${draftId}`);
      setClaim(r.data?.claim || null);
      setMedia(r.data?.media || []);
      setStepIdx(1);
    } catch (e) {
      toast.error(extractErr(e, "Could not load draft").message);
    }
  };

  // Discard with 5-second Undo via useUndoable. The wizard removes the
  // draft from the user's "your drafts" list optimistically; if the user
  // was actively in the wizard for that claim we also reset the steps.
  // If they hit Undo we re-fetch the drafts list (cheap, ~25 rows).
  const undoable = useUndoable();
  const discardClaim = (claimId) => {
    const id = claimId || claim?.id;
    if (!id) return;
    const isActive = id === claim?.id;
    undoable({
      message: "Draft discarded",
      description: "Permanent delete in 5 s — click Undo to restore.",
      delayMs: 5000,
      optimisticApply: () => {
        setMyDrafts((arr) => arr.filter((d) => d.id !== id));
        if (isActive) {
          setClaim(null);
          setMedia([]);
          setStepIdx(0);
          setSelected(null);
          // iter150AZ.69 — also wipe the localStorage snapshot so a
          // subsequent visit to the wizard doesn't auto-resume the
          // discarded draft.
          clearDraft("warranty");
        }
      },
      perform: () => api.delete(`/warranty/claims/${id}`),
      revert: () => refreshMyDrafts(),
      onUndo: () => toast.info("Draft restored."),
      testId: "warranty-wizard-discard-undo",
    });
  };

  const uploadFile = async (kind, file) => {
    if (!claim?.id || !file) return;
    setUploading(kind);
    // iter150AZ.68 — offline-safe upload. Route through the SW outbox
    // when disconnected; the FormData (Blob + fields) persists in
    // IndexedDB until the next online flush.
    const fd = new FormData();
    fd.append("upload", file);
    fd.append("kind", kind);
    const decision = await enqueueIfOffline(`/warranty/claims/${claim.id}/media`, {
      method: "POST",
      headers: { "X-User-Email": user?.email || "" },
      body:   fd,
    });
    if (decision === "queued") {
      // Optimistic local mutation so the wizard's "Continue" gate
      // unblocks without waiting for the server. We push a stub
      // media row with an offline id; the real row replaces it
      // once the SW flushes (the resume effect refetches the claim).
      const stub = {
        id: `offline-${Date.now()}`,
        kind, filename: file.name,
        mime_type: file.type || "application/octet-stream",
        size: file.size, _offline: true,
      };
      setMedia((m) => [...m, stub]);
      setClaim((c) => c ? {
        ...c,
        media_kinds_present: Array.from(new Set([...(c.media_kinds_present || []), kind])),
        _offline: true,
      } : c);
      // Nameplate SN preview needs server-side OCR — skip when offline.
      setUploading(null);
      return;
    }
    try {
      await api.post(`/warranty/claims/${claim.id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshClaim();
      // iter137.5 — auto-fire nameplate AI preview right after upload.
      // The user must confirm the extracted SN before we advance.
      if (kind === "nameplate") {
        try {
          setNameplateAi({ loading: true });
          const { data } = await api.post(`/warranty/claims/${claim.id}/preview-sn`);
          setNameplateAi({ loading: false, ...data });
        } catch (e) {
          setNameplateAi({ loading: false, error: extractErr(e).message });
        }
      }
    } catch (e) {
      toast.error(extractErr(e, "Upload failed").message);
    } finally {
      setUploading(null);
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!claim?.id) return;
    try {
      await api.delete(`/warranty/claims/${claim.id}/media/${mediaId}`);
      await refreshClaim();
    } catch (_) { /* ignore */ }
  };

  const saveFindings = async () => {
    if (!claim?.id) return;
    try {
      const r = await api.put(`/warranty/claims/${claim.id}`, {
        findings_text: findingsText,
        no_internet: noInternet,
      });
      setClaim(r.data);
    } catch (_) { /* ignore */ }
  };

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        clearInterval(recTickRef.current);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        // Send to backend for Whisper transcription.
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "findings.webm");
          const r = await api.post(`/warranty/claims/${claim.id}/transcribe`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const t = r.data?.transcript || "";
          const lang = (r.data?.language || "en").toLowerCase();
          if (r.data?.audio_url) setVoiceAudioUrl(r.data.audio_url);
          setTranscript(t);
          // Auto-merge into findingsText if empty so user can edit.
          if (!findingsText.trim()) setFindingsText(t);
          // iter137.5 — fire the KB-grounded analysis as soon as we
          // have a usable transcript. The AI replies in the same
          // language Whisper detected.
          if (t.length >= 25) {
            setVoiceAiLoading(true);
            try {
              const a = await api.post(`/warranty/claims/${claim.id}/voice-analyze`, {
                transcript: t, language: lang,
              });
              setVoiceAi(a.data);
            } catch (ee) {
              toast.error(extractErr(ee, "AI analysis failed").message);
            } finally {
              setVoiceAiLoading(false);
            }
          }
        } catch (e) {
          toast.error(extractErr(e, "Transcription failed").message);
        } finally {
          setTranscribing(false);
        }
        stream.getTracks().forEach((t) => t.stop());
      };
      recRef.current = mr;
      mr.start();
      setRecording({ active: true });
      setRecDuration(0);
      recTickRef.current = setInterval(() => setRecDuration((d) => d + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied or not available.");
    }
  };

  const stopRecord = () => {
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    setRecording(null);
  };

  const setEmergencyChoice = async (choice) => {
    setEmergency(choice);
    try {
      await api.put(`/warranty/claims/${claim.id}`, { emergency_action: choice });
      await refreshClaim();
    } catch (_) { /* ignore */ }
  };

  const submit = async () => {
    if (!claim?.id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Save latest text + flags.
      await saveFindings();
      const path = `/warranty/claims/${claim.id}/submit`;
      // iter121 — offline outbox: if we can't reach the network,
      // queue the submit so the SW replays it on reconnect. The
      // user gets a friendly toast + the wizard advances to a
      // "queued" terminal state so they're not stuck.
      const queued = await enqueueIfOffline(path, { method: "POST" });
      if (queued === "queued") {
        setFinalResult({ queued_offline: true, claim_id: claim.id });
        setStepIdx(STEPS.indexOf("done"));
        toast.info(t("outbox.warranty_queued", "Saved offline — your warranty claim will submit automatically when you reconnect."), { duration: 6000 });
        // iter150AZ.69 — terminal state reached; clear the local draft.
        clearDraft("warranty");
        return;
      }
      const r = await api.post(path);
      setFinalResult(r.data);
      setStepIdx(STEPS.indexOf("done"));
      clearDraft("warranty");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") setSubmitError(detail);
      else setSubmitError({ message: detail || e?.message || "Submit failed" });
    } finally {
      setSubmitting(false);
    }
  };

  // Forward navigation guard — make sure required slots have at least one upload.
  const requirementsOk = (kind) => {
    const list = mediaByKind[kind] || [];
    return list.length >= 1;
  };

  // iter150AG.photo-meas — voltage + amps now live in the dedicated
  // electrical_measurements collection rather than mediaByKind.
  // <WarrantyMeasurementsStep> owns the load/gate so we only track
  // the snapshot here to drive any global UI dependencies.
  const [measItems, setMeasItems] = useState([]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-slate-500 hover:text-blue-700"
                    data-testid="warranty-back-home">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <ShieldWarning size={20} weight="fill" className="text-rose-600" />
            <h1 className="text-base font-bold font-['Chivo']">{t("warranty_wizard.title", "Claim Warranty")}</h1>
            {claim?.ticket_number && (
              <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 border border-emerald-300 px-2 py-0.5 rounded-sm font-mono" data-testid="warranty-claim-number">
                {t("warranty_wizard.claim_no", "Claim #")} {claim.ticket_number}
              </span>
            )}
            {claim?.id && claim?.status === "draft" && step !== "done" && (
              <button
                onClick={() => discardClaim()}
                className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 border border-rose-200 hover:border-rose-400 px-2 py-1 rounded-sm bg-rose-50/40"
                data-testid="warranty-discard-draft"
                title={t("warranty_wizard.discard_title", "Delete this draft and start over")}
              >
                <Trash size={11} weight="fill" /> {t("warranty_wizard.discard_draft", "Discard draft")}
              </button>
            )}
          </div>
          <OfflineDraftsPill />
          <HelpPill />
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
        {/* Progress strip */}
        {claim && step !== "done" && (
          <div className="max-w-[1100px] mx-auto px-6 pb-2">
            <div className="flex gap-1 text-[10px] font-bold uppercase tracking-wider">
              {STEPS.slice(1, -1).map((s, i) => {
                const idx = i + 1;
                const active = idx === stepIdx;
                const done = idx < stepIdx;
                return (
                  <div key={s} className={`flex-1 h-1.5 rounded-sm ${
                    done ? "bg-emerald-500" : active ? "bg-blue-600" : "bg-slate-200"
                  }`} title={s} />
                );
              })}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-8">
        {/* STEP: pick */}
        {step === "pick" && (
          <div data-testid="warranty-step-pick">
            <h2 className="text-2xl font-black font-['Chivo'] mb-2">{t("warranty_wizard.pick_title", "File a warranty claim")}</h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("warranty_wizard.pick_intro", "Pick the compressor you're filing for. We'll walk you through the required photos & videos.")}
            </p>

            {/* Existing drafts — resume or discard */}
            {myDrafts.length > 0 && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-sm" data-testid="warranty-my-drafts">
                <div className="px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                  <FloppyDisk size={14} weight="fill" className="text-amber-700" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">
                    {t("warranty_wizard.drafts_title", "Your unfinished drafts")} ({myDrafts.length})
                  </h3>
                  <span className="text-[10px] text-amber-700 ml-auto">{t("warranty_wizard.drafts_hint", "Resume to keep going, or discard to start fresh.")}</span>
                </div>
                <ul className="divide-y divide-amber-100">
                  {myDrafts.map((d) => (
                    <li key={d.id} className="px-4 py-2 flex items-center gap-3 text-xs hover:bg-amber-100/40" data-testid={`warranty-draft-${d.id}`}>
                      <Wrench size={14} weight="fill" className="text-amber-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 truncate">
                          {d.product_name || t("warranty_wizard.compressor_label", "Compressor")} — SN {d.serial_number || d.serial_id}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {d.company_name || d.customer_id || "—"} ·
                          {" "}{t("warranty_wizard.draft_started", "started")} {(() => { try { return new Date(d.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); } catch { return d.created_at; } })()}
                        </div>
                      </div>
                      <button
                        onClick={() => resumeDraft(d.id)}
                        className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 px-2 py-1 rounded-sm hover:bg-blue-50"
                        data-testid={`warranty-draft-${d.id}-resume`}
                      >{t("warranty_wizard.draft_resume", "Resume")}</button>
                      <button
                        onClick={() => discardClaim(d.id)}
                        className="text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 px-2 py-1 rounded-sm hover:bg-rose-50 inline-flex items-center gap-1"
                        data-testid={`warranty-draft-${d.id}-discard`}
                      ><Trash size={10} weight="fill" /> {t("warranty_wizard.draft_discard", "Discard")}</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="relative mb-3">
              <MagnifyingGlass size={16} className="absolute left-3 top-3 text-slate-400" weight="bold" />
              <input
                data-testid="warranty-pick-search"
                type="search"
                value={pickQuery}
                onChange={(e) => setPickQuery(e.target.value)}
                placeholder={t("warranty_wizard.pick_search_placeholder", "Search by SN, model, or company name…")}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {fleet.loading && <div className="p-4 text-xs text-slate-400 flex items-center gap-2"><Spinner className="animate-spin" size={14} /> {t("warranty_wizard.searching", "Searching…")}</div>}
              {!fleet.loading && fleet.items.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-400">{t("warranty_wizard.no_matching_compressors", "No matching compressors found.")}</div>
              )}
              {fleet.items.map((c) => (
                <button
                  key={c.serial_id}
                  data-testid={`warranty-pick-item-${c.serial_id}`}
                  onClick={() => setSelected(c)}
                  className={`w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50 ${
                    selected?.serial_id === c.serial_id ? "bg-blue-50" : ""
                  }`}
                >
                  <Wrench size={16} weight="fill" className="text-violet-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{c.product_name || t("warranty_wizard.compressor_label", "Compressor")}</div>
                    <div className="text-xs text-slate-500 truncate">SN {c.serial_number || c.serial_id} · {c.company_name || c.customer_id || "—"}</div>
                  </div>
                  {selected?.serial_id === c.serial_id && <CheckCircle size={16} weight="fill" className="text-blue-600" />}
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                data-testid="warranty-pick-continue"
                disabled={!selected}
                onClick={startClaim}
                className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-40 flex items-center gap-2"
              >
                {t("warranty_wizard.continue", "Continue")} <ArrowRight size={13} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* STEP: media-capture (nameplate / installation_video / voltage / nearby_devices / working_hours / supporting / warranty_parts) */}
        {claim && (["nameplate", "installation_video", "voltage", "nearby_devices", "working_hours", "supporting", "warranty_parts"].includes(step)) && (
          <MediaStep
            kind={step}
            claim={claim}
            mediaList={mediaByKind[step] || []}
            onUpload={(file) => uploadFile(step, file)}
            uploading={uploading === step}
            onDelete={deleteMedia}
            noInternet={noInternet}
            onToggleNoInternet={(v) => { setNoInternet(v); api.put(`/warranty/claims/${claim.id}`, { no_internet: v }).catch(() => {}); }}
            cantTurnOn={cantTurnOn}
            onToggleCantTurnOn={(v) => {
              setCantTurnOn(v);
              api.put(`/warranty/claims/${claim.id}`, { cant_turn_on: v }).catch(() => {});
            }}
            nameplateAi={step === "nameplate" ? nameplateAi : null}
            onAcknowledgeNameplate={() => setNameplateAi((a) => a ? { ...a, acknowledged: true } : a)}
            evidenceSuggestions={step === "supporting" ? evidenceSuggestions : []}
            onLoadEvidenceSuggestions={async () => {
              try {
                const r = await api.post(`/warranty/claims/${claim.id}/evidence-suggest`);
                setEvidenceSuggestions(r.data?.items || []);
              } catch { /* noop */ }
            }}
            evidenceAgent={step === "supporting" ? evidenceAgent : null}
            evidenceAgentLoading={evidenceAgentLoading}
            onRunEvidenceAgent={async (representativeHelp = false) => {
              setEvidenceAgentLoading(true);
              try {
                const r = await api.post(`/warranty/claims/${claim.id}/evidence-agent`, {
                  findings_text: findingsText || null,
                  representative_help_requested: !!representativeHelp,
                });
                setEvidenceAgent(r.data || null);
              } catch (e) {
                // Surface the error inline rather than crashing the wizard.
                setEvidenceAgent({
                  error: e?.response?.data?.detail || "AI agent unavailable",
                  matched_rules: [], requested_evidence: [], override: false,
                });
              } finally { setEvidenceAgentLoading(false); }
            }}
            onBack={() => setStepIdx((i) => Math.max(0, i - 1))}
            onNext={() => setStepIdx((i) => i + 1)}
            canSkip={
              (step === "working_hours" && (!noInternet || cantTurnOn))
              || step === "supporting"
            }
            requireOk={
              requirementsOk(step)
              || (step === "working_hours" && (!noInternet || cantTurnOn))
              || step === "supporting"
            }
            requireNameplateAck={step === "nameplate" && nameplateAi && nameplateAi.extracted_sn && !nameplateAi.acknowledged}
          />
        )}

        {/* STEP: voltage + amps (iter150AG.photo-meas — photo-only,
            AI-extracted). Voltage is required; amps is optional in
            warranty claims because the compressor may not be runnable
            (the very reason for the claim). */}
        {claim && (step === "voltage" || step === "amps") && (
          <WarrantyMeasurementsStep
            step={step}
            claim={claim}
            email={(claim.created_by || "").trim()}
            measItems={measItems}
            onMeasItemsChange={setMeasItems}
            onBack={() => setStepIdx((i) => Math.max(0, i - 1))}
            onNext={() => setStepIdx((i) => i + 1)}
            t={t} />
        )}

        {/* STEP: findings */}
        {claim && step === "findings" && (
          <div data-testid="warranty-step-findings">
            <h2 className="text-2xl font-black font-['Chivo']">{t("warranty_wizard.findings_title", "Explain what happened")}</h2>
            <p className="text-sm text-slate-500 mb-6 max-w-[700px]">
              {t("warranty_wizard.findings_intro", "Type or record your technical explanation of the failure and why it should be covered under warranty.")}
              <span className="block text-rose-700 mt-2">
                {t("warranty_wizard.findings_warning_quote", "\"It's under warranty\"")} {t("warranty_wizard.findings_warning", "is")} <b>not</b> {t("warranty_wizard.findings_warning_suffix", "an explanation — describe what failed, when, and why you believe it's a warranty case.")}
              </span>
            </p>

            <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("warranty_wizard.voice_recording", "Voice recording (optional)")}</h4>
                {transcribing && <span className="text-xs text-blue-700 inline-flex items-center gap-1"><Spinner className="animate-spin" size={12} /> {t("warranty_wizard.transcribing", "Transcribing…")}</span>}
              </div>
              <div className="flex items-center gap-3">
                {!recording ? (
                  <button
                    data-testid="warranty-findings-record"
                    onClick={startRecord}
                    disabled={transcribing}
                    className="px-4 py-2 bg-rose-600 text-white rounded-sm text-xs font-bold uppercase hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    <Microphone size={13} weight="fill" /> {t("warranty_wizard.start_recording", "Start recording")}
                  </button>
                ) : (
                  <button
                    data-testid="warranty-findings-stop"
                    onClick={stopRecord}
                    className="px-4 py-2 bg-slate-800 text-white rounded-sm text-xs font-bold uppercase hover:bg-black inline-flex items-center gap-2 animate-pulse"
                  >
                    <Stop size={13} weight="fill" /> {t("warranty_wizard.stop_recording", "Stop")} · {fmtDuration(recDuration)}
                  </button>
                )}
                <span className="text-xs text-slate-500">
                  {t("warranty_wizard.rerecord_hint", "Re-record any time. Transcript will appear below — you can edit it before submitting.")}
                </span>
              </div>
              {transcript && !transcribing && (
                <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="warranty-findings-transcript">
                  <div className="font-bold uppercase text-[10px] tracking-wider mb-1 text-slate-500">{t("warranty_wizard.latest_transcript", "Latest transcript")}</div>
                  {transcript}
                </div>
              )}
            </div>

            {/* iter137.5 — KB-grounded AI feedback */}
            {(voiceAiLoading || voiceAi) && (
              <VoiceAiFeedback
                loading={voiceAiLoading}
                result={voiceAi}
                claim={claim}
                audioUrl={voiceAudioUrl || (claim?.findings_audio_gridfs_id
                  ? `/api/warranty/claims/${claim.id}/voice-audio` : null)}
                onFeedback={async (helpful) => {
                  try {
                    await api.post(`/warranty/claims/${claim.id}/voice-feedback`,
                      { helpful });
                  } catch (e) {
                    toast.error(extractErr(e, "Could not record feedback").message);
                    throw e;
                  }
                }}
              />
            )}

            <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                {t("warranty_wizard.findings_required_label", "Findings (required, ≥25 chars)")}
              </label>
              <textarea
                data-testid="warranty-findings-text"
                value={findingsText}
                onChange={(e) => setFindingsText(e.target.value)}
                rows={8}
                placeholder={t("warranty_wizard.findings_placeholder", "e.g. The airend started overheating at hour 4,217 even with new oil and a new separator. Discharge temp climbed from 195°F to 240°F within 2 hours under 100 psi load. The cooler is clean, fan is running, and ambient is 78°F. Suspect main bearing failure or oil-line restriction…")}
                className="w-full text-sm border border-slate-300 rounded-sm p-3 focus:outline-none focus:border-blue-500"
              />
              <div className="text-[11px] text-slate-400 mt-1">
                {findingsText.length} {t("warranty_wizard.findings_chars", "chars")} · {findingsText.length < 25 ? <span className="text-rose-600 font-bold">{t("warranty_wizard.findings_need_more", "need at least 25")}</span> : <span className="text-emerald-700 font-bold">{t("warranty_wizard.findings_ok", "OK")}</span>}
              </div>
            </div>

            <div className="flex justify-between gap-3">
              <button
                data-testid="warranty-findings-back"
                onClick={() => setStepIdx((i) => i - 1)}
                className="px-4 py-2 bg-white border border-slate-300 text-xs font-bold uppercase rounded-sm hover:bg-slate-50 inline-flex items-center gap-2"
              >
                <ArrowLeft size={13} weight="bold" /> {t("warranty_wizard.back", "Back")}
              </button>
              <div className="flex gap-2">
                <button
                  data-testid="warranty-findings-save"
                  onClick={async () => { await saveFindings(); }}
                  className="px-4 py-2 bg-white border border-slate-300 text-xs font-bold uppercase rounded-sm hover:bg-slate-50 inline-flex items-center gap-2"
                >
                  <FloppyDisk size={13} weight="bold" /> {t("warranty_wizard.save_draft", "Save draft")}
                </button>
                <button
                  data-testid="warranty-findings-next"
                  disabled={findingsText.trim().length < 25}
                  onClick={async () => { await saveFindings(); setStepIdx((i) => i + 1); }}
                  className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-40 inline-flex items-center gap-2"
                >
                  {t("warranty_wizard.continue", "Continue")} <ArrowRight size={13} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP: emergency */}
        {claim && step === "emergency" && (
          <div data-testid="warranty-step-emergency" className="max-w-[800px]">
            <h2 className="text-2xl font-black font-['Chivo'] mb-2">{t("warranty_wizard.emergency_title", "Emergency action note")}</h2>
            <p className="text-sm text-slate-500 mb-5">
              {t("warranty_wizard.emergency_intro", "Acknowledge before submitting your claim.")}
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-5 mb-5 rounded-sm">
              <div className="flex items-start gap-3">
                <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <div className="font-bold mb-2">{t("warranty_wizard.emergency_disclaimer_title", "Disclaimer")}</div>
                  <p>
                    {t("warranty_wizard.emergency_disclaimer_body", "If emergency action is necessary to prevent safety risk or major equipment damage, the End User or Dealer must perform filing the warranty claim through this app and may perform the work needed to revoke the compressor. Emergency work does not guarantee warranty approval or labor reimbursement.")}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <button
                data-testid="warranty-emergency-accept"
                onClick={() => { setEmergencyShownDisclaimer(true); setEmergencyChoice("accepted"); }}
                className={`p-5 rounded-sm border-2 text-left transition-colors ${
                  emergency === "accepted"
                    ? "border-rose-600 bg-rose-50"
                    : "border-slate-200 bg-white hover:border-rose-300"
                }`}
              >
                <Lightning size={22} weight="fill" className="text-rose-600 mb-2" />
                <div className="font-bold mb-1">{t("warranty_wizard.emergency_yes_title", "Yes — emergency action accepted")}</div>
                <div className="text-xs text-slate-500">
                  {t("warranty_wizard.emergency_yes_desc", "Critical safety / damage risk. I have read the disclaimer and understand emergency work doesn't guarantee approval.")}
                </div>
              </button>
              <button
                data-testid="warranty-emergency-refuse"
                onClick={() => { setEmergencyShownDisclaimer(true); setEmergencyChoice("refused"); }}
                className={`p-5 rounded-sm border-2 text-left transition-colors ${
                  emergency === "refused"
                    ? "border-slate-700 bg-slate-100"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <CheckCircle size={22} weight="fill" className="text-slate-600 mb-2" />
                <div className="font-bold mb-1">{t("warranty_wizard.emergency_no_title", "No — not an emergency")}</div>
                <div className="text-xs text-slate-500">
                  {t("warranty_wizard.emergency_no_desc", "Standard warranty review. I will wait for ACC service to schedule the work.")}
                </div>
              </button>
            </div>

            <div className="flex justify-between gap-3 mt-6">
              <button
                onClick={() => setStepIdx((i) => i - 1)}
                className="px-4 py-2 bg-white border border-slate-300 text-xs font-bold uppercase rounded-sm hover:bg-slate-50 inline-flex items-center gap-2"
              >
                <ArrowLeft size={13} weight="bold" /> {t("warranty_wizard.back", "Back")}
              </button>
              <button
                data-testid="warranty-emergency-next"
                disabled={!emergency}
                onClick={() => setStepIdx((i) => i + 1)}
                className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-40 inline-flex items-center gap-2"
              >
                {t("warranty_wizard.review_btn", "Review")} <ArrowRight size={13} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* STEP: review */}
        {claim && step === "review" && (
          <div data-testid="warranty-step-review">
            <h2 className="text-2xl font-black font-['Chivo'] mb-2">{t("warranty_wizard.review_title", "Review & submit")}</h2>
            <p className="text-sm text-slate-500 mb-5">
              {t("warranty_wizard.review_intro", "Once submitted, ACC service department and you will receive an automated email with a ticket number.")}
            </p>

            <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div><div className="text-[11px] uppercase font-bold text-slate-400">{t("warranty_wizard.review_compressor", "Compressor")}</div><div>{claim.product_name} · SN {claim.serial_number || claim.serial_id}</div></div>
              <div><div className="text-[11px] uppercase font-bold text-slate-400">{t("warranty_wizard.review_customer", "Customer")}</div><div>{claim.company_name || claim.customer_id}</div></div>
              <div><div className="text-[11px] uppercase font-bold text-slate-400">{t("warranty_wizard.review_emergency", "Emergency action")}</div>
                <div className={emergency === "accepted" ? "text-rose-700 font-bold" : "text-slate-700"}>
                  {emergency === "accepted" ? t("warranty_wizard.review_emergency_accepted", "Accepted (work may proceed)") : emergency === "refused" ? t("warranty_wizard.review_emergency_refused", "Refused (no emergency)") : t("warranty_wizard.review_emergency_not_ack", "Not acknowledged")}
                </div></div>
              <div><div className="text-[11px] uppercase font-bold text-slate-400">{t("warranty_wizard.review_smartcom", "SmartCom / connected")}</div><div>{noInternet ? t("warranty_wizard.review_smartcom_no", "No (working hours photo provided)") : t("warranty_wizard.review_smartcom_yes", "Yes / unknown")}</div></div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
              <div className="text-[11px] uppercase font-bold text-slate-400 mb-2">{t("warranty_wizard.review_findings", "Findings")}</div>
              <div className="text-sm whitespace-pre-wrap">{findingsText || <span className="text-rose-700">{t("warranty_wizard.review_missing", "Missing")}</span>}</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
              <div className="text-[11px] uppercase font-bold text-slate-400 mb-2">{t("warranty_wizard.review_captures", "Captures uploaded")}</div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {Object.entries(KIND_META).map(([kind, meta]) => {
                  const list = mediaByKind[kind] || [];
                  const required = ["nameplate", "installation_video", "voltage", "nearby_devices"].includes(kind)
                    || (kind === "working_hours" && noInternet);
                  return (
                    <div key={kind} className="flex items-center gap-2">
                      {list.length > 0
                        ? <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                        : required
                          ? <XCircle size={14} weight="fill" className="text-rose-600" />
                          : <Info size={14} weight="bold" className="text-slate-400" />}
                      <span>{meta.label}</span>
                      <span className="text-xs text-slate-500">· {list.length}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {submitError && (
              <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-sm p-3 mb-4" data-testid="warranty-submit-error">
                <WarningCircle size={14} weight="fill" className="inline mr-1" />
                {submitError.message || JSON.stringify(submitError)}
              </div>
            )}

            <div className="flex justify-between gap-3">
              <button
                onClick={() => setStepIdx((i) => i - 1)}
                className="px-4 py-2 bg-white border border-slate-300 text-xs font-bold uppercase rounded-sm hover:bg-slate-50 inline-flex items-center gap-2"
              >
                <ArrowLeft size={13} weight="bold" /> {t("warranty_wizard.back", "Back")}
              </button>
              <button
                data-testid="warranty-submit-btn"
                disabled={submitting}
                onClick={submit}
                className="px-6 py-2 bg-emerald-600 text-white text-xs font-bold uppercase rounded-sm hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
              >
                {submitting ? <Spinner className="animate-spin" size={13} weight="bold" /> : <PaperPlaneTilt size={13} weight="bold" />}
                {submitting ? t("warranty_wizard.submitting", "Submitting…") : t("warranty_wizard.submit_btn", "Submit warranty claim")}
              </button>
            </div>
          </div>
        )}

        {/* STEP: done */}
        {step === "done" && finalResult && (
          <DoneScreen result={finalResult} claim={claim} onHome={() => navigate("/")} />
        )}
      </main>
    </div>
  );
}

function MediaStep({ kind, claim, mediaList, onUpload, uploading, onDelete, noInternet, onToggleNoInternet, cantTurnOn, onToggleCantTurnOn, nameplateAi, onAcknowledgeNameplate, evidenceSuggestions, onLoadEvidenceSuggestions, evidenceAgent, evidenceAgentLoading, onRunEvidenceAgent, onBack, onNext, canSkip, requireOk, requireNameplateAck }) {
  const meta = KIND_META[kind];
  const t = useT();
  const [camOpen, setCamOpen] = useState(false);
  // For capture-mode the user picks photo vs video at the moment they
  // tap the capture button. ``supporting`` and ``warranty_parts`` allow
  // either; everything else is locked to its kind.
  const lockedMode = meta.isVideo ? "video" : (meta.accept.includes("video") ? null : "photo");
  const [pickedMode, setPickedMode] = useState(lockedMode);

  // iter137.5 — auto-load evidence suggestions when entering the
  // supporting step (cheap, runs once per mount).
  // iter142 — also fire the AI evidence agent (rule-driven required
  // evidence + AI coaching) on first entry. Skipped if the user has
  // already requested a representative override.
  useEffect(() => {
    if (kind === "supporting" && onLoadEvidenceSuggestions) {
      onLoadEvidenceSuggestions();
    }
    if (kind === "supporting" && onRunEvidenceAgent && !evidenceAgent) {
      onRunEvidenceAgent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const Icon = meta.isVideo ? VideoCamera : Camera;
  const captureDisabled = uploading || (!meta.multiple && mediaList.length >= 1)
    || (kind === "working_hours" && cantTurnOn);

  return (
    <div data-testid={`warranty-step-${kind}`}>
      <h2 className="text-2xl font-black font-['Chivo']">{meta.label}</h2>
      <p className="text-sm text-slate-500 mb-5 max-w-[760px]">{meta.desc}</p>

      {/* iter137.5 — Nameplate AI confirmation */}
      {kind === "nameplate" && nameplateAi && (
        <NameplateAiBanner
          ai={nameplateAi}
          claim={claim}
          onAcknowledge={onAcknowledgeNameplate}
        />
      )}

      {kind === "working_hours" && (
        <div className="space-y-2 mb-4">
          <div className="bg-rose-50 border border-rose-200 rounded-sm p-4 flex items-start gap-3">
            <input
              type="checkbox"
              id="cto"
              checked={cantTurnOn}
              onChange={(e) => onToggleCantTurnOn?.(e.target.checked)}
              data-testid="warranty-cant-turn-on-toggle"
              className="w-4 h-4 mt-0.5"
            />
            <label htmlFor="cto" className="text-sm leading-snug">
              <b>{t("wcw.cant_turn_on_title", "The compressor cannot be turned on.")}</b>
              <span className="block text-xs text-slate-600 mt-0.5">
                {t("wcw.cant_turn_on_help", "Tick this if the unit will not power up — the working-hours photo step will be skipped because the controller display is unreadable. The reviewer will see this flag in the claim.")}
              </span>
            </label>
          </div>
          {!cantTurnOn && (
            <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
              <input
                type="checkbox"
                id="ni"
                checked={noInternet}
                onChange={(e) => onToggleNoInternet(e.target.checked)}
                data-testid="warranty-no-internet-toggle"
                className="w-4 h-4"
              />
              <label htmlFor="ni" className="text-sm">
                <b>{t("warranty_wizard.no_internet_label", "This compressor cannot connect to the internet / SmartCom.")}</b> I will provide the working-hours photo below.
              </label>
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        {mediaList.map((m) => (
          <div key={m.id} data-testid={`warranty-media-item-${m.id}`}
               className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
              {m.mime_type?.startsWith("video/") ? (
                // iter150AZ.61 — AuthVideo fetches with X-User-Email so
                // the recorded warranty-claim clip plays back inside
                // PWA / localStorage-only sessions where the legacy
                // ``<video src>`` produced an empty player.
                <AuthVideo src={`/warranty/media/${m.id}`}
                       className="w-full h-full object-cover"
                       testId={`warranty-media-video-${m.id}`} />
              ) : m.mime_type?.startsWith("image/") ? (
                // iter150AZ.60 — AuthImage fetches with X-User-Email so
                // the picture renders even when there's no session
                // cookie (e.g. localStorage-only auth in PWA mode).
                <AuthImage src={`/warranty/media/${m.id}`} alt={m.filename} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={32} weight="duotone" className="text-slate-400" />
              )}
            </div>
            <div className="p-2 flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-500 truncate flex-1">{m.filename}</div>
              <button onClick={() => onDelete(m.id)} className="text-rose-600 hover:text-rose-800" data-testid={`warranty-media-delete-${m.id}`}>
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
        {!captureDisabled && (
          <div className="aspect-video bg-white border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 transition-colors p-3 text-center">
            {uploading ? (
              <Spinner size={28} className="animate-spin text-blue-600" />
            ) : (
              <>
                <Icon size={28} weight="bold" className="text-slate-500" />
                <div className="text-xs font-bold uppercase">{t("wcw.live_capture_only", "Live capture only")}</div>
                <div className="text-[10px] text-slate-400 px-2">
                  {t("wcw.live_capture_hint", "No file uploads — point your camera at the subject and capture.")}
                </div>
                {!lockedMode ? (
                  <div className="flex gap-1.5 mt-1">
                    <button
                      data-testid={`warranty-capture-photo-${kind}`}
                      onClick={() => { setPickedMode("photo"); setCamOpen(true); }}
                      className="px-3 py-1.5 bg-blue-700 text-white text-[11px] font-bold uppercase rounded-sm inline-flex items-center gap-1.5 hover:bg-blue-800"
                    >
                      <Camera size={12} weight="bold" /> {t("wcw.btn_photo", "Photo")}
                    </button>
                    <button
                      data-testid={`warranty-capture-video-${kind}`}
                      onClick={() => { setPickedMode("video"); setCamOpen(true); }}
                      className="px-3 py-1.5 bg-rose-600 text-white text-[11px] font-bold uppercase rounded-sm inline-flex items-center gap-1.5 hover:bg-rose-700"
                    >
                      <VideoCamera size={12} weight="bold" /> {t("wcw.btn_video", "Video")}
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid={`warranty-capture-${kind}`}
                    onClick={() => { setPickedMode(lockedMode); setCamOpen(true); }}
                    className="mt-1 px-4 py-1.5 bg-blue-700 text-white text-[11px] font-bold uppercase rounded-sm inline-flex items-center gap-1.5 hover:bg-blue-800"
                  >
                    <Icon size={12} weight="bold" /> {t("wcw.btn_open_camera", "Open camera")}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <LiveCameraCapture
        open={camOpen}
        mode={pickedMode || "photo"}
        maxSeconds={120}
        onClose={() => setCamOpen(false)}
        onCapture={(blob, info) => {
          // Wrap the blob in a File so the existing upload pipeline (FormData)
          // works unchanged. We invent a filename + mime so the backend's
          // extension allow-list accepts it.
          const f = new File([blob], info.filename, { type: info.mime_type });
          onUpload(f);
        }}
        testIdPrefix={`warranty-livecam-${kind}`}
      />

      {kind === "voltage" && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4 text-xs text-blue-800 flex items-start gap-2">
          <Info size={14} weight="fill" className="shrink-0 mt-0.5" />
          <span>{t("warranty_wizard.voltage_best_practice", "Best practice: capture all")} <b>three</b> phase-to-phase readings (L1↔L2, L2↔L3, L1↔L3). One reading is the minimum.</span>
        </div>
      )}

      {/* iter137.5 — Supporting evidence library suggestions */}
      {kind === "supporting" && evidenceSuggestions && evidenceSuggestions.length > 0 && (
        <EvidenceSuggestionsCard items={evidenceSuggestions} />
      )}

      {/* iter142 — AI evidence agent (rule-driven) + representative override */}
      {kind === "supporting" && (
        <EvidenceAgentCard
          loading={evidenceAgentLoading}
          result={evidenceAgent}
          onRetry={() => onRunEvidenceAgent && onRunEvidenceAgent(false)}
          onRequestRepresentative={() => onRunEvidenceAgent && onRunEvidenceAgent(true)}
        />
      )}

      <div className="flex justify-between gap-3">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-white border border-slate-300 text-xs font-bold uppercase rounded-sm hover:bg-slate-50 inline-flex items-center gap-2"
          data-testid={`warranty-${kind}-back`}
        >
          <ArrowLeft size={13} weight="bold" /> {t("warranty_wizard.back", "Back")}
        </button>
        <button
          data-testid={`warranty-${kind}-next`}
          disabled={(!requireOk && !canSkip) || requireNameplateAck}
          onClick={onNext}
          className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-40 inline-flex items-center gap-2"
          title={requireNameplateAck ? "Confirm the AI-extracted serial number first" : ""}
        >
          {canSkip && mediaList.length === 0 ? t("warranty_wizard.skip", "Skip") : t("warranty_wizard.continue", "Continue")} <ArrowRight size={13} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function DoneScreen({ result, claim, onHome }) {
  const parts = result?.parts || {};
  const ai = result?.ai || {};
  const pmQuote = result?.pm_quote || null;
  const missing = parts.missing || [];
  const purchased = parts.purchased || [];
  const t = useT();
  // iter121 — when offline, show a queued-confirmation screen instead
  // of the normal "ticket #X created" UI. The SW will replay the
  // submit on reconnect and the user gets a toast at that point.
  if (result?.queued_offline) {
    return (
      <div data-testid="warranty-step-done-queued" className="max-w-[840px]">
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6 text-center">
          <CalendarBlank size={48} weight="fill" className="text-amber-600 mx-auto mb-3" />
          <h2 className="text-2xl font-black font-['Chivo'] mb-2">
            {t("warranty_wizard.done_queued_title", "Saved offline — will submit when you reconnect")}
          </h2>
          <p className="text-sm text-slate-700 mb-4">
            {t("warranty_wizard.done_queued_body", "Your warranty claim is safely stored on this device. As soon as your connection comes back, we'll submit it to ACC automatically and you'll get a toast with your claim number.")}
          </p>
          <p className="text-[11px] text-slate-500 italic mb-4">
            {t("warranty_wizard.done_queued_hint", "You can close the app — the submission will happen in the background.")}
          </p>
          <button
            onClick={onHome}
            className="px-5 py-2.5 bg-slate-900 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-slate-700"
            data-testid="warranty-done-queued-home"
          >
            {t("warranty_wizard.done_back_home", "Back to home")}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="warranty-step-done" className="max-w-[840px]">
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 mb-6 text-center">
        <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto mb-3" />
        <h2 className="text-2xl font-black font-['Chivo'] mb-2">{t("warranty_wizard.done_submitted", "Claim submitted")}</h2>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 mb-1">{t("warranty_wizard.your_claim_number", "Your claim number")}</div>
        <div className="text-3xl font-black font-mono text-emerald-700 mb-2" data-testid="warranty-done-ticket">{result.ticket_number}</div>
        <div className="text-[11px] text-slate-500 mb-2 italic">{t("warranty_wizard.reference_this", "Reference this number in any emails, calls, or labor-coverage paperwork with ACC and your dealer.")}</div>
        <div className="text-sm text-slate-600">{t("warranty_wizard.done_email_sent", "Email sent to ACC service department, you, and your account contacts.")}</div>
      </div>

      {pmQuote?.quote_id && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-5 mb-5" data-testid="warranty-done-pm-quote">
          <div className="flex items-start gap-3">
            <Package size={22} weight="fill" className="text-blue-600 mt-1 shrink-0" />
            <div className="flex-1">
              <h3 className="font-black text-blue-900 mb-1">{t("warranty_wizard.done_pm_quote_title", "Draft quote ready for missing PM parts")}</h3>
              <p className="text-xs text-blue-800 mb-3">
                {t("warranty_wizard.done_pm_quote_intro", "We auto-created quote")} <b className="font-mono">{pmQuote.quote_id}</b> with <b>{pmQuote.line_count} line(s)</b> totalling <b>{pmQuote.currency} {Number(pmQuote.total).toFixed(2)}</b>.
                After-sales will reach out to confirm — or you can review and pay it now.
              </p>
              <button
                data-testid="warranty-done-open-quote"
                onClick={() => window.open(`/quote/${pmQuote.quote_id}`, "_blank", "noopener,noreferrer")}
                className="px-4 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800"
              >
                {t("warranty_wizard.done_open_quote", "Open quote")}
              </button>
            </div>
          </div>
        </div>
      )}

      {missing.length > 0 && !pmQuote?.quote_id && (
        <div className="bg-rose-50 border-l-4 border-rose-500 rounded-sm p-5 mb-5">
          <div className="flex items-start gap-3">
            <Package size={22} weight="fill" className="text-rose-600 mt-1 shrink-0" />
            <div>
              <h3 className="font-black text-rose-900 mb-1">{missing.length} {t("warranty_wizard.done_pm_missing", "PM part(s) missing")}</h3>
              <p className="text-xs text-rose-800 mb-3">
                <b>{t("warranty_wizard.done_no_free_warranty", "No free warranty service")}</b> can be dispatched until these are ordered. Please add the missing parts to your cart now —
                an after-sales tech will follow up to confirm.
              </p>
              <ul className="text-xs space-y-1 mb-3" data-testid="warranty-done-missing">
                {missing.slice(0, 10).map((m, i) => (
                  <li key={i}>• {m.product_name} <span className="text-slate-500">(qty {m.qty})</span></li>
                ))}
              </ul>
              <button
                data-testid="warranty-done-order-parts"
                onClick={() => window.open(`/compressors/${encodeURIComponent(claim?.serial_id || "")}`, "_self")}
                className="px-4 py-2 bg-rose-600 text-white text-xs font-bold uppercase rounded-sm hover:bg-rose-700"
              >
                {t("warranty_wizard.done_order_parts", "Order parts now")}
              </button>
            </div>
          </div>
        </div>
      )}

      {purchased.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
          <h3 className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">{t("warranty_wizard.done_parts_purchased", "Parts purchased so far")} ({purchased.length})</h3>
          <ul className="text-xs space-y-1" data-testid="warranty-done-purchased">
            {purchased.slice(0, 12).map((p, i) => (
              <li key={i}>• {p.product_name} × {p.qty}</li>
            ))}
            {purchased.length > 12 && <li className="text-slate-400">…and {purchased.length - 12} more</li>}
          </ul>
        </div>
      )}

      {ai?.summary && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
          <h3 className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">{t("warranty_wizard.done_ai_analysis", "AI analysis (advisory only)")}</h3>
          <p className="text-sm mb-2">{ai.summary}</p>
          {(ai.possible_causes || []).length > 0 && (
            <ul className="text-xs space-y-1 ml-4 list-disc">
              {ai.possible_causes.slice(0, 5).map((c, i) => (
                <li key={i}><b>[{(c.probability || "").toUpperCase()}]</b> {c.cause} — <i>{c.diagnostic}</i></li>
              ))}
            </ul>
          )}
          <div className="text-[11px] text-slate-500 italic mt-2">{ai.disclaimer}</div>
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <a
          href={claim?.id ? `/warranty/claims/${claim.id}/chat` : "#"}
          data-testid="warranty-done-open-chat"
          className="px-5 py-2 bg-violet-600 text-white text-xs font-bold uppercase rounded-sm hover:bg-violet-700 inline-flex items-center gap-1.5"
        >
          {t("warranty_wizard.done_open_chat", "Open chat with tech")}
        </a>
        <button
          data-testid="warranty-done-home"
          onClick={onHome}
          className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800"
        >
          {t("warranty_wizard.done_back_home", "Back to home")}
        </button>
      </div>
    </div>
  );
}

function fmtDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}



// ─── iter137.5 helpers ─────────────────────────────────────────────

function NameplateAiBanner({ ai, claim, onAcknowledge }) {
  const t = useT();
  if (ai.loading) {
    return (
      <div data-testid="warranty-nameplate-ai-loading"
        className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4 flex items-center gap-2 text-xs text-blue-800">
        <Spinner size={14} className="animate-spin" />
        {t("ai.nameplate_loading", "AI is reading the serial number from your nameplate photo…")}
      </div>
    );
  }
  if (ai.error) {
    return (
      <div data-testid="warranty-nameplate-ai-error"
        className="bg-rose-50 border border-rose-200 rounded-sm p-4 mb-4 text-xs text-rose-800">
        AI nameplate read failed: {ai.error}. Re-shoot the photo if the SN is unclear.
      </div>
    );
  }
  if (!ai.extracted_sn) return null;
  const ok = !!ai.sn_match;
  const expected = claim?.serial_number || ai.expected_sn || "";
  return (
    <div
      data-testid="warranty-nameplate-ai-banner"
      className={`border-2 rounded-sm p-4 mb-4 ${ok ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
    >
      <div className="flex items-start gap-2 mb-2">
        {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5" />
            : <Warning size={18} weight="fill" className="text-amber-600 mt-0.5" />}
        <div>
          <div className={`text-sm font-bold ${ok ? "text-emerald-800" : "text-amber-800"}`}>
            {ok ? t("ai.nameplate_ok", "AI confirmed the serial number") : t("ai.nameplate_mismatch", "AI-read serial does not match the selected compressor")}
          </div>
          <div className="text-xs text-slate-700 mt-1">
            AI: <b className="font-mono">{ai.extracted_sn}</b>
            {expected && (<> · SN: <b className="font-mono">{expected}</b></>)}
            {ai.confidence != null && (<> · {Math.round(100 * ai.confidence)}%</>)}
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-700 mb-2">
        {ok
          ? t("ai.nameplate_confirm_help_ok", "Please confirm the read is correct before continuing.")
          : t("ai.nameplate_confirm_help_bad", "If the AI reading is wrong, retake the photo with better lighting/framing. If the AI is right but the SN doesn't match the chosen compressor, go Back and pick the correct unit.")}
      </p>
      {!ai.acknowledged && (
        <button
          data-testid="warranty-nameplate-ai-acknowledge"
          onClick={onAcknowledge}
          className="px-3 py-1.5 bg-emerald-700 text-white text-[11px] font-bold uppercase rounded-sm hover:bg-emerald-800 inline-flex items-center gap-1.5"
        >
          <CheckCircle size={12} weight="bold" /> {t("ai.nameplate_confirm_btn", "Confirm SN and continue")}
        </button>
      )}
      {ai.acknowledged && (
        <div className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1">
          <CheckCircle size={12} weight="fill" /> {t("ai.nameplate_confirmed_pill", "Confirmed")}
        </div>
      )}
    </div>
  );
}

function VoiceAiFeedback({ loading, result, claim, audioUrl, onFeedback }) {
  const t = useT();
  const audioRef = useRef(null);
  const [voted, setVoted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const sendVote = async (helpful) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onFeedback?.(helpful);
      setVoted(helpful);
    } finally {
      setSubmitting(false);
    }
  };
  const playAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    try { a.currentTime = 0; a.play(); } catch { /* noop */ }
  };
  if (loading) {
    return (
      <div data-testid="warranty-voiceai-loading"
        className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4 flex items-center gap-2 text-xs text-blue-800">
        <Spinner size={14} className="animate-spin" />
        {t("ai.voice_loading", "AI is reviewing your description against the manufacturer knowledge base…")}
      </div>
    );
  }
  if (!result) return null;
  const causes = result.probable_causes || [];
  const steps = result.troubleshooting_steps || [];
  const sources = result.kb_sources || [];
  const lang = (result.language || "en").toUpperCase();
  const kbSuffix = result.kb_used
    ? t("ai.voice_kb_used_suffix", "grounded in your KB")
    : t("ai.voice_kb_unused_suffix", "no KB matched");
  return (
    <div data-testid="warranty-voiceai-result"
      className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={14} weight="fill" className="text-violet-600" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
          {t("ai.voice_title", "AI feedback")} ({lang} · {kbSuffix})
        </h4>
        {audioUrl && (
          <button
            type="button"
            data-testid="warranty-voiceai-listen"
            onClick={playAudio}
            className="px-2 py-1 text-[11px] font-bold uppercase rounded-sm bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1 text-slate-700"
          >
            🔊 {t("ai.voice_listen_again", "Listen again")}
          </button>
        )}
      </div>
      {/* Hidden audio element streamed from the backend */}
      {audioUrl && (
        <audio ref={audioRef} src={`${process.env.REACT_APP_BACKEND_URL}${audioUrl}`} preload="none" />
      )}
      {result.customer_summary && (
        <p className="text-sm text-slate-800 mb-3">{result.customer_summary}</p>
      )}
      {causes.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("ai.voice_probable_causes", "Probable causes")}</div>
          <ul className="text-xs text-slate-700 space-y-1 list-disc pl-5">
            {causes.slice(0, 5).map((c, i) => (
              <li key={i}>
                {c.cause}
                {c.confidence != null && <span className="text-slate-400"> · {Math.round(100 * c.confidence)}%</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {steps.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("ai.voice_steps", "Try these steps")}</div>
          <ol className="text-xs text-slate-700 space-y-1 list-decimal pl-5">
            {steps.slice(0, 8).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}
      {sources.length > 0 && (
        <div className="text-[10px] text-slate-400 leading-snug">
          {t("ai.voice_kb_sources", "Knowledge sources")}: {sources.filter((s) => s.ok).map((s) => s.label || s.url).join(" · ") || "—"}
        </div>
      )}
      <div className="text-[10px] text-slate-400 italic mt-2">
        {t("ai.voice_advisory", "Advisory only — final coverage decision rests with the warranty reviewer.")}
      </div>
      {/* Thumbs feedback */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
        <span className="text-[11px] text-slate-500 font-semibold mr-1">
          {t("ai.voice_helpful_q", "Was this analysis helpful?")}
        </span>
        {voted === null ? (
          <>
            <button
              type="button"
              data-testid="warranty-voiceai-thumbs-up"
              disabled={submitting}
              onClick={() => sendVote(true)}
              className="px-2.5 py-1 text-[11px] font-bold rounded-sm bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 disabled:opacity-50"
            >👍 {t("ai.voice_helpful_yes", "Helpful")}</button>
            <button
              type="button"
              data-testid="warranty-voiceai-thumbs-down"
              disabled={submitting}
              onClick={() => sendVote(false)}
              className="px-2.5 py-1 text-[11px] font-bold rounded-sm bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 disabled:opacity-50"
            >👎 {t("ai.voice_helpful_no", "Not helpful")}</button>
          </>
        ) : (
          <span data-testid="warranty-voiceai-thanks" className="text-[11px] text-slate-600">
            {voted ? "👍" : "👎"} {t("ai.voice_thanks", "Thanks for the feedback.")}
          </span>
        )}
      </div>
    </div>
  );
}

function EvidenceSuggestionsCard({ items }) {
  const t = useT();
  return (
    <div data-testid="warranty-evidence-suggestions"
      className="bg-violet-50 border border-violet-200 rounded-sm p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={13} weight="fill" className="text-violet-700" />
        <div className="text-xs font-bold uppercase tracking-wider text-violet-800">
          {t("ai.evidence_title", "Reference evidence based on your description")}
        </div>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-violet-100 rounded-sm p-2.5">
            <div className="flex items-center gap-2">
              <div className="text-xs font-bold text-slate-800 flex-1">{it.title}</div>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-violet-100 text-violet-700">
                {it.media_type || "text"}
              </span>
            </div>
            {it.description && <div className="text-[11px] text-slate-600 mt-1 leading-snug">{it.description}</div>}
            {it.url && (
              <a href={it.url} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] text-violet-700 underline mt-1 inline-block">
                {t("ai.evidence_open", "Open reference")}
              </a>
            )}
            {it.matched_keywords?.length > 0 && (
              <div className="text-[10px] text-slate-400 mt-1">
                {t("ai.evidence_matched", "Matched")}: {it.matched_keywords.join(", ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EvidenceAgentCard({ loading, result, onRetry, onRequestRepresentative }) {
  // iter142 — Rule-driven AI evidence agent. Shows the user a checklist
  // of additional evidence required by admin-defined rules that match
  // their findings, plus a "Need a representative to help me" override
  // button that suppresses the AI prompt and signals a human review.
  if (loading && !result) {
    return (
      <div data-testid="warranty-evidence-agent-loading"
           className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 text-xs text-amber-900 flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        <span>AI is checking what extra evidence may help your claim…</span>
      </div>
    );
  }
  if (!result) return null;

  if (result.override) {
    return (
      <div data-testid="warranty-evidence-agent-override"
           className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4 text-xs text-blue-900">
        <strong>Representative requested.</strong> The AI evidence prompts
        are paused. A technician will review your claim and reach out if
        more evidence is needed.{" "}
        <button onClick={onRetry} data-testid="warranty-evidence-agent-undo-override"
                className="underline ml-1">Resume AI suggestions</button>
      </div>
    );
  }
  if (result.error) {
    return (
      <div data-testid="warranty-evidence-agent-error"
           className="bg-rose-50 border border-rose-200 rounded-sm p-3 mb-4 text-xs text-rose-800 flex items-start gap-2">
        <span className="flex-1">AI evidence agent unavailable: {result.error}</span>
        <button onClick={onRetry} className="underline">Retry</button>
      </div>
    );
  }
  const matched = result.matched_rules || [];
  const requested = result.requested_evidence || [];
  if (matched.length === 0 && requested.length === 0) {
    return (
      <div data-testid="warranty-evidence-agent-noop"
           className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-4 text-xs text-emerald-900">
        AI agent didn't find any extra evidence requirements for your description. You're good to go.
      </div>
    );
  }
  return (
    <div data-testid="warranty-evidence-agent"
         className="bg-amber-50 border border-amber-300 rounded-sm p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={13} weight="fill" className="text-amber-700" />
        <div className="text-xs font-bold uppercase tracking-wider text-amber-900">
          AI requested additional evidence
        </div>
      </div>
      {matched.map((m) => (
        m.ai_instructions ? (
          <div key={m.rule_id} className="bg-white border-l-2 border-amber-400 px-3 py-2 mb-2 text-[11px] text-slate-800 italic">
            <span className="font-bold not-italic">{m.label}: </span>{m.ai_instructions}
          </div>
        ) : null
      ))}
      {requested.length > 0 && (
        <ul className="space-y-1 text-xs text-amber-900" data-testid="warranty-evidence-agent-list">
          {requested.map((r, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="font-bold">{r.count}×</span>
              <span>
                <span className="font-bold">{r.label || r.kind}</span>
                {r.instructions ? <> — {r.instructions}</> : null}
                {r.kind ? <span className="ml-1 text-[10px] uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-200 text-amber-900">{r.kind}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-amber-200">
        <button onClick={onRetry}
                data-testid="warranty-evidence-agent-retry"
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-amber-300 text-amber-800 rounded-sm hover:bg-amber-100">
          Re-run AI check
        </button>
        <EvidenceAgentSpeakBtn matched={matched} requested={requested} />
        <button onClick={onRequestRepresentative}
                data-testid="warranty-evidence-agent-representative"
                className="ml-auto px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-blue-300 text-blue-800 rounded-sm hover:bg-blue-50">
          I don't understand — ask a representative for help
        </button>
      </div>
    </div>
  );
}

function EvidenceAgentSpeakBtn({ matched, requested }) {
  // Browser-native Web Speech Synthesis — picks a voice that matches the
  // active i18n lang (or the document lang) when one is installed on the
  // OS. No network call, no API key. Best effort: silently no-op if the
  // browser doesn't support speechSynthesis.
  const t = useT();
  const { lang } = useI18n();
  const [speaking, setSpeaking] = useState(false);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  if (!supported) return null;

  const buildScript = () => {
    const parts = [];
    matched.forEach((m) => {
      if (m.ai_instructions) parts.push(`${m.label}. ${m.ai_instructions}`);
    });
    requested.forEach((r) => {
      const head = `${r.count || 1}. ${r.label || r.kind}`;
      const tail = r.instructions ? `: ${r.instructions}` : "";
      parts.push(`${head}${tail}`);
    });
    return parts.join(". ");
  };

  const voiceForLang = (l) => {
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      const prefix = (l || "en").slice(0, 2);
      return voices.find((v) => (v.lang || "").toLowerCase().startsWith(prefix)) || null;
    } catch { return null; }
  };

  const speak = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false); return;
    }
    const text = buildScript();
    if (!text.trim()) return;
    const utt = new window.SpeechSynthesisUtterance(text);
    utt.lang = ({ en: "en-US", fr: "fr-FR", es: "es-ES" }[lang] || "en-US");
    const v = voiceForLang(lang);
    if (v) utt.voice = v;
    utt.rate = 1.0; utt.pitch = 1.0;
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
    setSpeaking(true);
  };

  return (
    <button onClick={speak}
            data-testid="warranty-evidence-agent-speak"
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-amber-300 text-amber-800 rounded-sm hover:bg-amber-100 inline-flex items-center gap-1">
      {speaking
        ? t("ai.evidence_speak_stop", "🔇 Stop")
        : t("ai.evidence_speak", "🔊 Listen")}
    </button>
  );
}



// ─────────────────────────────────────────────────────────────────────
// WarrantyMeasurementsStep — iter150AG photo-only voltage + amps for
// the warranty claim wizard. Voltage is required; amps is OPTIONAL per
// the user's design rule (the compressor may not be runnable when a
// claim is being filed). Re-uses the shared <MeasurementsSet>
// component from /components/MeasurementCapture.jsx.
// ─────────────────────────────────────────────────────────────────────
function WarrantyMeasurementsStep({
  step, claim, email, measItems, onMeasItemsChange,
  onBack, onNext, t,
}) {
  const phaseCount   = _phaseCountFromClaim(claim);
  const isVoltage    = step === "voltage";
  const isAmps       = step === "amps";

  // Only the slots for THIS step gate the Next button.
  const slotsForStep = (measItems || []).filter(m =>
    isVoltage ? m.kind === "voltage" : m.kind === "amps");
  const voltageOk    = isMeasurementsSetComplete(slotsForStep, phaseCount, false);
  // iter150AZ.77 — offline stubs count as "captured" for amps too.
  // The wizard's purpose is to record evidence; AI evaluation happens
  // server-side after sync.
  const _ampsCaptured = (s) => slotsForStep.some(
    m => m.slot === s && (m._offline === true || m.final_value != null));
  const ampsOk       = phaseCount === 1
    ? _ampsCaptured("amps:running:single")
    : ["amps:running:l1", "amps:running:l2", "amps:running:l3"]
        .every(_ampsCaptured);

  const canContinue = isVoltage ? voltageOk : true;  // amps is optional

  return (
    <div data-testid={`warranty-step-${step}`} className="space-y-4">
      <header>
        <h2 className="text-2xl font-black font-['Chivo']">
          {isVoltage
            ? t("warranty_wizard.voltage_title", "Voltage measurements")
            : t("warranty_wizard.amps_title", "Current (amps) measurements")}
        </h2>
        <p className="text-sm text-slate-500 mt-1 max-w-[700px]">
          {isVoltage
            ? (phaseCount === 1
                ? t("warranty_wizard.voltage_intro_1p",
                    "Take one photo of the meter LCD while the compressor is running, and one while stopped. AI will read the values for you.")
                : t("warranty_wizard.voltage_intro_3p",
                    "Phase-to-phase readings — L1↔L2, L2↔L3, L1↔L3 — once running and once stopped (6 photos). AI extracts the value from each photo."))
            : t("warranty_wizard.amps_intro_optional",
                "OPTIONAL. Only required if the compressor still runs. Capture the amps reading on each line while the compressor is running.")}
        </p>
      </header>

      <MeasurementsSet
        parentKind="warranty_claim"
        parentId={claim.id}
        phaseCount={phaseCount}
        nominalVoltage={phaseCount === 1 ? 240 : 400}
        email={email}
        ampsRequired={false}
        initialItems={measItems}
        onChange={onMeasItemsChange}
      />

      {/* Render only the slots relevant to the current step. The
          <MeasurementsSet> above renders ALL slots; we let the operator
          see the full picture but only gate the Next button on what
          the current step requires. */}
      <div className="text-[11px] text-slate-500">
        {isVoltage
          ? <>Voltage progress: {(slotsForStep.filter(m => m._offline === true || m.final_value != null).length)} / {(phaseCount === 1 ? 2 : 6)}</>
          : <>Amps progress: {(slotsForStep.filter(m => m._offline === true || m.final_value != null).length)} / {(phaseCount === 1 ? 1 : 3)} (optional)</>}
        {isAmps && ampsOk && (
          <span className="ml-2 text-emerald-700 font-bold">✓ all captured</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button data-testid={`warranty-${step}-back`} onClick={onBack}
                className="px-3 py-2 text-sm border rounded">
          {t("warranty_wizard.action_back", "Back")}
        </button>
        <button data-testid={`warranty-${step}-next`} onClick={onNext}
                disabled={!canContinue}
                title={!canContinue ? "Take every required photo first" : ""}
                className="ml-auto px-4 py-2 bg-blue-600 text-white rounded font-bold text-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {isVoltage
            ? t("warranty_wizard.action_next", "Continue")
            : (ampsOk
                ? t("warranty_wizard.action_next", "Continue")
                : t("warranty_wizard.action_skip", "Skip — compressor not running"))}
        </button>
      </div>
    </div>
  );
}

